/*
  THE GIFT LAUNCHER IS DRESSED AS THE CHAT BUTTON'S PAIR (2026-10-21, the
  owner: "make the gift launcher visually identical to the current chat
  button - same white disc, same shadow, same border, same icon weight -
  just with a gift glyph. It should read as a pair with the chat, not as a
  foreign widget sitting next to it").

  WHY A SCRIPT AND NOT A STYLESHEET. The Loloyal launcher paints nothing in
  the page's DOM: #loloyal-launcher-box computes transparent, holds one
  hidden span, and has no background, image, mask or pseudo-element - and
  hiding it still removed all 72,325 green pixels of the disc. The disc and
  the glyph live in a SHADOW ROOT on that box, which page CSS cannot reach
  into whatever its specificity. The root is open, so a stylesheet appended
  inside it can, and that is exactly how bc-mobile-chat-launcher.js already
  parks Inbox's own activator inside ITS shadow root - the same house
  pattern, not a new trick.

  WHAT IS IN THERE, read off the live widget:

    #loloyal-launcher-box            the host, 60x60, scaled by the theme
      #shadow-root (open)
        <style> ... .loloyal-launcher{background:#a2caa2;border-radius:30px;
                    padding:0 20px;gap:12px} ... </style>
        div.loloyal-launcher-frame
          div.loloyal-launcher-container
            div.loloyal-launcher      <- the disc
              img.loloyal-launcher-close-icon
              img.loloyal-launcher-icon   <- the glyph, a white 30px image

  So the disc takes the chat button's own white, hairline and shadow, and
  the glyph - a white image, invisible the moment the disc turns white - is
  replaced through `content` with a stroked gift in the chat icon's ink.
  Replacing it rather than filtering it is what buys the owner's "same icon
  weight": theirs is a solid silhouette, the chat's is a 2px outline, and no
  filter turns one into the other. If the swap ever fails the browser falls
  back to the element's own src, which is the app's glyph on a white disc -
  wrong, but never blank.

  Phone only, because the desktop launcher is the app's own and the brief
  leaves it alone.
*/
(() => {
  'use strict';

  // Every width since 2026-10-29 (the owner: "the three icons on the right,
  // chat, rewards and Google, like on the mobile version") - the desktop gift
  // is dressed the same white disc. The name is kept; it no longer narrows.
  const MOBILE = window.matchMedia('all');
  const HOST_SELECTOR = '#loloyal-launcher-box, .loloyal-launcher-box';
  const STYLE_ID = 'bc-gift-pair';
  const ARM_ATTR = 'data-bc-gift-dressing';
  const DRESSED_CLASS = 'bc-gift-dressed';
  const MAX_TRIES = 240;
  const RETRY_MS = 60;
  const REVEAL_MS = 1200;
  let tries = 0;
  let observer = null;
  let watchdog = null;

  // A gift in the chat glyph's weight: 24px box, 2px stroke, round joins,
  // #333 - the colour bc-mobile-chat-launcher.css gives its own icon.
  const GIFT = "url(\"data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='10' width='18' height='11' rx='1.5'/%3E%3Crect x='2' y='6.5' width='20' height='3.5' rx='1.2'/%3E%3Cpath d='M12 6.5v14.5'/%3E%3Cpath d='M12 6.5S10.6 2.8 8.2 3c-2.6 0.2-2.6 3.5 0 3.5H12z'/%3E%3Cpath d='M12 6.5s1.4-3.7 3.8-3.5c2.6 0.2 2.6 3.5 0 3.5H12z'/%3E%3C/svg%3E\")";

  const CSS = [
    '.loloyal-launcher {',
    '  background: #fff !important;',
    '  border: 1px solid rgba(0, 0, 0, 0.14) !important;',
    '  border-radius: 999px !important;',
    '  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.10) !important;',
    '  padding: 0 !important;',
    '  gap: 0 !important;',
    '  box-sizing: border-box !important;',
    '}',
    /* The press state their sheet draws is a 10% black wash; on white that
       reads as the grey a pressed button should be, so it is left alone. */
    '.loloyal-launcher-icon {',
    '  width: 28px !important;',
    '  height: 28px !important;',
    '  content: ' + GIFT + ' !important;',
    '}'
  ].join('\n');

  function dress(host) {
    const root = host && host.shadowRoot;
    if (!root) return false;
    let style = root.getElementById ? root.getElementById(STYLE_ID) : null;
    if (!style) {
      style = root.querySelector('#' + STYLE_ID);
    }
    if (style) {
      if (style.textContent !== CSS) style.textContent = CSS;
      return true;
    }
    style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    // Last in the root, so it settles the same declarations their own sheet
    // makes without a specificity argument.
    root.appendChild(style);
    return true;
  }

  function watch(host) {
    if (observer || !host.shadowRoot || !window.MutationObserver) return;
    // Their widget re-renders its own shadow content (the launcher turns
    // into a close button while the panel is open), which would take the
    // stylesheet with it.
    observer = new MutationObserver(function() { dress(host); });
    observer.observe(host.shadowRoot, { childList: true, subtree: false });
  }

  /*
    THE WHITE DISC HAS TO BE THE FIRST ONE PAINTED (2026-10-22, the owner:
    "the gift button appears green first and only then turns white ... the
    shopper sees the app's styling for a moment every single load").

    Measured as it stood, three cold loads at 430: the app's host lands
    16.1-19.3s into the page (it is the last thing this homepage loads) and
    our white landed 549, 662 and 879ms after it - so the green disc was on
    screen for 520, 630 and 850ms. Two causes, and both are fixed here:
    the dressing was driven by a 400ms poll, and nothing hid the disc while
    it waited.

    So the host is held INVISIBLE until it is dressed, and the holding rule
    lives in assets/bc-mobile-chat-launcher.css keyed on an attribute THIS
    SCRIPT sets. That direction matters: if the script never runs - blocked,
    parse error, desktop - the attribute is never set, the rule never
    matches and the gift simply shows in the app's own colours. A stylesheet
    that hid it on its own could strand it invisible for ever.

    On top of that, two timers make the hidden window short and bounded: the
    host is caught by a MutationObserver the moment it lands (with a 60ms
    backstop, because the shadow root is sometimes attached a tick after the
    host), and a watchdog reveals it REVEAL_MS after it is first seen
    whatever happens. visibility, not display, so the box is reserved and
    nothing on the page moves when it appears.
  */
  function arm() {
    if (!MOBILE.matches) return;
    document.documentElement.setAttribute(ARM_ATTR, '');
  }

  function disarm() {
    document.documentElement.removeAttribute(ARM_ATTR);
  }

  function seen(host) {
    if (host.classList.contains(DRESSED_CLASS)) return true;
    if (dress(host)) {
      host.classList.add(DRESSED_CLASS);
      if (watchdog) { window.clearTimeout(watchdog); watchdog = null; }
      watch(host);
      return true;
    }
    // Seen but not dressable yet (the shadow root lands a tick later). Start
    // the clock that guarantees it cannot stay invisible.
    if (!watchdog) watchdog = window.setTimeout(disarm, REVEAL_MS);
    return false;
  }

  function attempt() {
    if (!MOBILE.matches) return;
    const host = document.querySelector(HOST_SELECTOR);
    if (host && seen(host)) return;
    tries += 1;
    if (tries < MAX_TRIES) window.setTimeout(attempt, RETRY_MS);
  }

  function hunt() {
    if (!window.MutationObserver) return;
    // The host is inserted into the page long after load; this catches it in
    // the same frame instead of up to RETRY_MS later.
    const hunter = new MutationObserver(function () {
      if (!MOBILE.matches) return;
      const host = document.querySelector(HOST_SELECTOR);
      if (host && seen(host)) hunter.disconnect();
    });
    hunter.observe(document.documentElement, { childList: true, subtree: true });
  }

  function start() {
    arm();
    attempt();
    hunt();
    if (MOBILE.addEventListener) {
      MOBILE.addEventListener('change', function() {
        tries = 0;
        arm();
        attempt();
      });
    }
    document.addEventListener('shopify:section:load', function() {
      tries = 0;
      attempt();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
